import { ArrowLeft } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ButtonCmp from 'src/components/ButtonCmp';
import ConfirmationModalCmp from 'src/components/ConfirmModal/ConfirmationModalCmp';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import StepperLayout from 'src/components/StepperLayout/StepperLayout';
import { PATH } from 'src/constants/path';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import WalToast from 'src/utils/WalToast';

import { ADD_QUOTE_KEYS, ADD_QUOTE_STEPS } from './addQuote.const';
import { IAddQuoteStep } from './addQuote.interface';
import { addQuoteCurrentStep, addQuoteCustomerBillingAccountId, addQuoteDivisionId, addQuoteServiceType, addQuoteStopCount, resetAddQuoteData } from './AddQuote.slice';
import { isStopStepKey, stopPositionFromKey } from './multiStop.helpers';
import Sidebar from './Sidebar';
import ConsigneeDetails from './Steps/ConsigneeDetails';
import AddDimensions from './Steps/Dimensions';
import FtlLoadShipmentDetails from './Steps/FtlLoadShipmentDetails';
import ShipmentDetails from './Steps/ShipmentDetails';
import ShipperDetails from './Steps/ShipperDetails';
import StopDetails from './Steps/StopDetails';
import { StepperPersistContext } from './Steps/useStepperPersist';

const NewAddQuote = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const stepKey = useAppSelector(addQuoteCurrentStep);
    const selectedDivisionId = useAppSelector(addQuoteDivisionId);
    const selectedBillingAccountId = useAppSelector(addQuoteCustomerBillingAccountId);
    const serviceType = useAppSelector(addQuoteServiceType);
    const stopCount = useAppSelector(addQuoteStopCount);

    const [currentStep, setCurrentStep] = useState<IAddQuoteStep>(ADD_QUOTE_STEPS(t, serviceType, stopCount)[0]);
    const [headerChildren, setHeaderChildren] = useState<React.ReactNode>(null);
    const [isShowLeaveConfirmation, setIsShowLeaveConfirmation] = useState(false);

    // Holds the active step's "persist my in-flight values" callback. Each step registers it via
    // useRegisterStepperPersist; Sidebar invokes it before switching steps so sideways navigation
    // keeps whatever the user had already typed on the step they are leaving.
    const persistRef = useRef<(() => void) | null>(null);

    // Keep currentStep in sync with Redux step key, serviceType, and stopCount
    useEffect(() => {
        const step = ADD_QUOTE_STEPS(t, serviceType, stopCount).find((s) => s.key === stepKey);

        if (step) {
            setCurrentStep(step);
        }
    }, [stepKey, serviceType, stopCount]);

    // Guard: division + billing account must be selected before entering stepper
    useEffect(() => {
        if (!selectedDivisionId || !selectedBillingAccountId) {
            WalToast.error(t('QuotingHub.QuotingDashboard.Division_and_billing_account_required'));
            navigate(PATH.MY_QUOTE, { replace: true });
        }
    }, []);

    const renderStepComponent = () => {
        // Multi-stop route (stopCount > 2): render by step KEY instead of the fixed step
        // NUMBER switch below (that switch assumes exactly 4/3 steps at fixed positions,
        // which no longer holds once middle stops are inserted). Returns BEFORE the
        // existing switch so the stopCount===2 path below is completely untouched.
        if (stopCount > 2) {
            if (currentStep.key === ADD_QUOTE_KEYS.SHIPPER_DETAILS) return <ShipperDetails setHeaderChildren={setHeaderChildren} />;

            if (isStopStepKey(currentStep.key)) {
                return <StopDetails key={currentStep.key} position={stopPositionFromKey(currentStep.key)} setHeaderChildren={setHeaderChildren} />;
            }

            if (currentStep.key === ADD_QUOTE_KEYS.CONSIGNEE_DETAILS) return <ConsigneeDetails setHeaderChildren={setHeaderChildren} />;

            if (currentStep.key === ADD_QUOTE_KEYS.SHIPMENT_DETAILS) {
                return serviceType === 'ftl' ? <FtlLoadShipmentDetails setHeaderChildren={setHeaderChildren} /> : <ShipmentDetails setHeaderChildren={setHeaderChildren} />;
            }

            if (currentStep.key === ADD_QUOTE_KEYS.DIMENSIONS) return <AddDimensions setHeaderChildren={setHeaderChildren} />;

            return null;
        }

        // ——— legacy switch below, untouched ———
        switch (currentStep.step) {
            case 1:
                return <ShipperDetails setHeaderChildren={setHeaderChildren} />;
            case 2:
                return <ConsigneeDetails setHeaderChildren={setHeaderChildren} />;
            case 3:
                // FTL: Load & Shipment Details | LTL: Set Shipment Details
                if (serviceType === 'ftl') {
                    return <FtlLoadShipmentDetails setHeaderChildren={setHeaderChildren} />;
                }

                return <ShipmentDetails setHeaderChildren={setHeaderChildren} />;
            case 4:
                return <AddDimensions setHeaderChildren={setHeaderChildren} />;
            default:
                return null;
        }
    };

    // The header arrow is the only control that abandons the whole stepper — the per-step Back
    // buttons and the sidebar just move between steps and must never prompt. So the confirmation
    // lives here and nowhere else.
    const handleBack = () => {
        setIsShowLeaveConfirmation(true);
    };

    const handleLeaveWithoutSaving = () => {
        setIsShowLeaveConfirmation(false);
        const fromNewQuote = (location.state as any)?.fromNewQuote === true;
        dispatch(resetAddQuoteData({ clearAll: false, openModal: fromNewQuote }));
        navigate(PATH.MY_QUOTE, { replace: true });
    };

    return (
        <PageSectionLayout
            headerClassName="stepper-container"
            contentClassName="stepper-container overflow-y-auto"
            header={
                <Header
                    title={
                        <div className="flex items-center gap-x-2.5">
                            <ButtonCmp className="btn_secondary_black" size="square-sm" onClick={handleBack}>
                                <ArrowLeft className="w-4 h-4" />
                            </ButtonCmp>
                            Add Quote
                        </div>
                    }
                />
            }
        >
            <StepperPersistContext.Provider value={persistRef}>
                <StepperLayout
                    Sidebar={<Sidebar currentStep={currentStep} serviceType={serviceType} stopCount={stopCount} />}
                    title={currentStep.screenTitle}
                    description={currentStep.screenDescription}
                    headerChildren={headerChildren}
                >
                    {renderStepComponent()}
                </StepperLayout>
            </StepperPersistContext.Provider>

            {isShowLeaveConfirmation && (
                <ConfirmationModalCmp
                    title={t('QuotingHub.Common.Discard_Changes')}
                    description={t('QuotingHub.Common.Discard_Changes_Desc')}
                    handleClose={() => setIsShowLeaveConfirmation(false)}
                    isPrimaryDangerBtn={true}
                    dangerBtnText={t('QuotingHub.Common.Leave_Without_Saving')}
                    handleSubmit={handleLeaveWithoutSaving}
                    secondaryBtnText={t('QuotingHub.Common.Stay')}
                    handleSecondaryBtn={() => setIsShowLeaveConfirmation(false)}
                />
            )}
        </PageSectionLayout>
    );
};

export default NewAddQuote;
